import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button, Popup } from "devextreme-react";
import { confirm } from "devextreme/ui/dialog";
import DynamicFromLookup from "../../../../dynamicFormBuilder/DynamicFromLookup";
import { IDWarehouseLookupLayout } from "../../../../common/lookups/Layouts/IdLayouts";
import { IMLocationAvailabilityLookupLayout } from "../../../../common/lookups/Layouts/ImLayouts";
import { MESSAGES } from "../../../../common/Messages";
import { wareHouseField, locationField } from "../Layout";

// WMD0049 - "WM Crates Detail From Location".
//
// Not a screen of its own: WMM0063 creates it in code from the crate detail grid and shows it modally
// before the line is written (uWMM0063.pas GetFromLocation). Putting something in a crate, or taking it
// out, is physically a location move - the crate header supplies one end and this asks for the other.
// Cancelling it aborts the add or the delete, which is why onOk is the only way out that proceeds.
//
// The same form serves both directions; Delphi swaps the caption and the memo on DetailType = 'M'.

export const CrateLocationMode = {
    Add: "add",
    Remove: "remove",
};

// Which key the location lookup is narrowed on, by the item's tracking level. Delphi's if/else-if chain
// sets the lookup's BaseWhere once, so a sub-lot line is keyed on its sub-lot alone - never additionally
// on its item or lot (uWMD0049.PAS LocationLookup).
const ITEM_TYPE_KEYS = {
    I: "RItem",
    L: "RLot",
    S: "RSublot",
    U: "RUnit",
};

const CrateDetailFromLocation = ({ visible, mode, detail, module, onOk, onCancel }) => {
    const [formFields, setFormFields] = useState({ RWarehouse: null, RLocation: null });

    // Bumped when picking a location fills the warehouse in behind the operator's back. The lookup
    // resolves its display text once on mount, so without a remount a second pick in a different
    // warehouse would leave the old warehouse name on screen.
    const [warehouseRenderKey, setWarehouseRenderKey] = useState(0);

    // DynamicFromLookup hands its text box back through fieldRef and calls it without checking, so a
    // lookup in grid display mode has to supply one whether or not the caller wants the reference.
    const fieldRefs = useRef({});
    const setFieldRef = (name, element) => {
        if (element) fieldRefs.current[name] = element;
    };

    const isRemove = mode === CrateLocationMode.Remove;
    const itemType = detail?.ItemType;
    const lookupKey = ITEM_TYPE_KEYS[itemType];
    const lookupKeyValue = lookupKey ? detail?.[lookupKey] : null;

    // Delphi starts from a blank record every time (cdsFromLocation.EmptyDataSet / Insert), so a
    // location chosen for the previous line is never carried into the next one.
    useEffect(() => {
        if (!visible) return;
        setFormFields({ RWarehouse: null, RLocation: null });
        setWarehouseRenderKey((key) => key + 1);
    }, [visible, lookupKeyValue]);

    const runtimeParameter = JSON.stringify([{ RWarehouse: "RWarehouse" }]);

    const locationLayout = useMemo(
        () =>
            IMLocationAvailabilityLookupLayout({
                moduleType: module,
                runtimeParameter,
                itemType: itemType ?? "",
                itemId: detail?.RItem ?? "",
                lotId: detail?.RLot ?? "",
                sublotId: detail?.RSublot ?? "",
                unitId: detail?.RUnit ?? "",
            }),
        [module, runtimeParameter, itemType, detail?.RItem, detail?.RLot, detail?.RSublot, detail?.RUnit]
    );

    const handleFieldChange = (name, value) => {
        setFormFields((previous) => {
            // Changing the warehouse clears the location, because the location that was chosen almost
            // certainly does not belong to the new warehouse (cdsFromLocationP_WarehouseChange).
            if (name === wareHouseField.name && value !== previous.RWarehouse) {
                return { ...previous, RWarehouse: value, RLocation: null };
            }
            return { ...previous, [name]: value };
        });
    };

    // Picking a location fills in the warehouse it sits in (cdsFromLocationP_LocationChange). The
    // lookup returns the warehouse's RowId, which is what the warehouse field and the location
    // lookup's own warehouse filter are keyed on - its code is only what the operator reads.
    const handleSelectionChange = (name, row) => {
        if (name !== locationField.name || !row) return;

        const warehouseRowId = row.rwarehouse ? row.rwarehouse.toString().trim() : null;
        setFormFields((previous) => {
            if (previous.RWarehouse === warehouseRowId) return previous;
            setWarehouseRenderKey((key) => key + 1);
            return { ...previous, RWarehouse: warehouseRowId };
        });
    };

    // Delphi asks about a blank Location OR a blank warehouse, never both - the second question is
    // only reachable when a location was chosen without one (btnOKClick).
    const handleOk = async () => {
        if (!formFields.RLocation) {
            if (!(await confirm(MESSAGES.BLANK_LOCATION_CONFIRM, MESSAGES.WARNING))) return;
        } else if (!formFields.RWarehouse) {
            if (!(await confirm(MESSAGES.BLANK_WAREHOUSE_CONFIRM, MESSAGES.WARNING))) return;
        }

        if (onOk) onOk({ RLocation: formFields.RLocation, RWarehouse: formFields.RWarehouse });
    };

    if (!visible) return null;

    return (
        <Popup
            visible={visible}
            onHiding={onCancel}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title="WM Crates Detail From Location (WMD0049)"
            container=".dx-viewport"
            width="30vw"
            height="auto"
        >
            <div
                style={{
                    border: "1px solid #999",
                    padding: "8px 10px",
                    fontWeight: "bold",
                    fontSize: "12px",
                    textAlign: "center",
                    marginBottom: "10px",
                }}
            >
                {isRemove ? MESSAGES.CRATE_REMOVE_LOCATION_EXPLANATION : MESSAGES.CRATE_ADD_LOCATION_EXPLANATION}
            </div>

            <div
                style={{
                    backgroundColor: window._env_.Color2,
                    color: "#FFFFFF",
                    fontWeight: "bold",
                    fontSize: "13px",
                    textAlign: "center",
                    padding: "3px",
                    marginBottom: "10px",
                }}
            >
                {isRemove ? MESSAGES.CRATE_TO_LOCATION_TITLE : MESSAGES.CRATE_FROM_LOCATION_TITLE}
            </div>

            <div className="formfield-mb-2">
                <DynamicFromLookup
                    key={`warehouse-${warehouseRenderKey}`}
                    fieldRef={(el) => setFieldRef(wareHouseField.name, el)}
                    name={wareHouseField.name}
                    required={false}
                    field={wareHouseField}
                    formFields={formFields}
                    layout={IDWarehouseLookupLayout}
                    onValueChanged={(e) => handleFieldChange(wareHouseField.name, e.value)}
                    handleSelectionChange={handleSelectionChange}
                    editMode={true}
                />
            </div>
            {/* Delphi keys the Location lookup on the detail's item type and falls back to a BaseWhere of
                (0=1) when it cannot - so an unkeyable line offers no locations rather than all of them. It
                says so at the moment the lookup is opened, not before. This was raised as a modal when the
                dialog appeared, which fired on every incomplete line and read as an error the operator had
                caused; said quietly beside the field it explains the empty lookup without blocking. */}
            {!lookupKeyValue && (
                <div style={{ fontSize: "11px", color: "#a94442", marginBottom: "4px" }}>
                    {MESSAGES.CRATE_LOCATION_ITEM_TYPE_MISSING}
                </div>
            )}
            <div className="formfield-mb-2">
                <DynamicFromLookup
                    fieldRef={(el) => setFieldRef(locationField.name, el)}
                    name={locationField.name}
                    required={false}
                    field={locationField}
                    formFields={formFields}
                    layout={locationLayout}
                    onValueChanged={(e) => handleFieldChange(locationField.name, e.value)}
                    handleSelectionChange={handleSelectionChange}
                    editMode={true}
                />
            </div>

            <div className="formfield-mb-2 center-button">
                <Button text={MESSAGES.OK ?? "OK"} type="default" onClick={handleOk} />
                <div style={{ marginLeft: "1rem" }}>
                    <Button text="Cancel" onClick={onCancel} />
                </div>
            </div>
        </Popup>
    );
};

export default CrateDetailFromLocation;
